import { useEffect, useRef } from "react";

type Candle = {
  x: number;
  open: number;
  close: number;
  high: number;
  low: number;
  up: boolean;
  born: number;
  w: number;
};

const LIFETIME = 3200;
const STEP = 13;
const MAX = 90;
const POS = "22, 194, 100";
const NEG = "226, 58, 58";

/** Canvas candle tape that draws along the cursor path, then fades — PerpsGO style. */
export function CursorCandles() {
  const canvasRef = useRef<HTMLCanvasElement | null>(null);

  useEffect(() => {
    if (window.matchMedia("(pointer: coarse)").matches) return;
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    if (!ctx) return;

    let dpr = Math.min(window.devicePixelRatio || 1, 2);
    const resize = () => {
      dpr = Math.min(window.devicePixelRatio || 1, 2);
      canvas.width = Math.floor(window.innerWidth * dpr);
      canvas.height = Math.floor(window.innerHeight * dpr);
      canvas.style.width = `${window.innerWidth}px`;
      canvas.style.height = `${window.innerHeight}px`;
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    };
    resize();

    const candles: Candle[] = [];
    let lastX = -9999;
    let lastY = -9999;

    const onMove = (e: PointerEvent) => {
      if (lastX === -9999) {
        lastX = e.clientX;
        lastY = e.clientY;
        return;
      }
      if (Math.abs(e.clientX - lastX) < STEP) return;

      const prevY = lastY;
      lastX = e.clientX;
      lastY = e.clientY;

      // Screen Y is inverted: moving up = bullish.
      const open = prevY;
      const close = e.clientY;
      const up = close <= open;
      const span = Math.max(Math.abs(close - open), 6);
      const high = Math.min(open, close) - (4 + Math.random() * span * 0.7);
      const low = Math.max(open, close) + (4 + Math.random() * span * 0.7);

      candles.push({
        x: e.clientX,
        open,
        close,
        high,
        low,
        up,
        born: performance.now(),
        w: 7,
      });
      if (candles.length > MAX) candles.splice(0, candles.length - MAX);
    };

    let raf = 0;
    const draw = () => {
      const now = performance.now();
      ctx.clearRect(0, 0, window.innerWidth, window.innerHeight);

      for (let i = candles.length - 1; i >= 0; i--) {
        const c = candles[i];
        const age = now - c.born;
        if (age > LIFETIME) {
          candles.splice(i, 1);
          continue;
        }
        const t = age / LIFETIME;
        const alpha = (1 - t) * (1 - t);
        const grow = Math.min(1, age / 120);
        const rgb = c.up ? POS : NEG;
        const cx = Math.round(c.x) + 0.5;

        ctx.globalAlpha = alpha;
        ctx.strokeStyle = `rgba(${rgb},1)`;
        ctx.lineWidth = 1;

        // wick
        ctx.beginPath();
        ctx.moveTo(cx, c.high);
        ctx.lineTo(cx, c.low);
        ctx.stroke();

        // body
        const top = Math.min(c.open, c.close);
        const h = Math.max(Math.abs(c.close - c.open) * grow, 2);
        const x = cx - c.w / 2;
        if (c.up) {
          // hollow bullish
          ctx.strokeRect(x, top, c.w, h);
        } else {
          ctx.fillStyle = `rgba(${rgb},0.9)`;
          ctx.fillRect(x, top, c.w, h);
        }
      }
      ctx.globalAlpha = 1;
      raf = requestAnimationFrame(draw);
    };
    raf = requestAnimationFrame(draw);

    window.addEventListener("pointermove", onMove, { passive: true });
    window.addEventListener("resize", resize);
    return () => {
      cancelAnimationFrame(raf);
      window.removeEventListener("pointermove", onMove);
      window.removeEventListener("resize", resize);
    };
  }, []);

  return (
    <canvas
      ref={canvasRef}
      aria-hidden="true"
      className="pointer-events-none fixed inset-0 z-30"
    />
  );
}
